"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent, type PointerEvent } from "react";
import { IconChevronLeft, IconChevronRight } from "@/components/ui/icons";
import { fmt } from "@/i18n/config";
import type { Dictionary } from "@/i18n/dictionaries/tr";

// Tur detayı fotoğraf kaydırıcısı (docs/design-system.md → "Tur galerisi"). Kütüphane yok; WAI-ARIA APG
// "Auto-Rotating Image Carousel" desenine göre, WCAG 2.2 AA:
//  - kendiliğinden geçiş 6 sn (ürün sahibi isteği); görünür Durdur/Başlat düğmesi (2.2.2);
//  - fare üzerindeyken, klavye odağı içerideyken, sekme gizliyken ve kaydırıcı ekranda değilken bekler;
//  - kullanıcı önceki/sonraki/nokta ile gezinince otomatik geçiş durur (yeniden "Başlat" ile);
//  - prefers-reduced-motion: reduce → kendiliğinden geçmez, geçişler animasyonsuz;
//  - canlı bölge: dönerken aria-live="off", dururken "polite"; etkin olmayan slaytlar inert + aria-hidden;
//  - ←/→/Home/End (odak kaydırıcıdayken), dokunmatikte kaydırma.
// İlk fotoğraf LCP'dir (eager + fetchpriority high); diğerleri yalnızca sırası yaklaşınca yüklenir. Sabit en-boy oranı → CLS yok.

const INTERVAL_MS = 6000;
const SWIPE_PX = 40;
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
const noopSubscribe = () => () => {};

export type GalleryImage = { src: string; alt: string };

export function TourGallery({
  images,
  labels,
  sizes,
  className = "",
}: {
  images: GalleryImage[];
  labels: Dictionary["gallery"];
  sizes: string;
  className?: string;
}) {
  const total = images.length;
  const [index, setIndexState] = useState(0);
  // Gösterilmiş slaytlar yüklü kalır (geri dönüşte yeniden çizim yok)
  const [seen, setSeen] = useState<ReadonlySet<number>>(() => new Set([0]));
  const setIndex = useCallback((next: number) => {
    setIndexState(next);
    setSeen((prev) => (prev.has(next) ? prev : new Set([...prev, next])));
  }, []);
  // Hareket tercihi yalnızca tarayıcıda bilinir: sunucu çiziminde "azaltılmış" varsayılır (otomatik geçiş yok),
  // hidrasyondan sonra gerçek değer. Kullanıcı düğmeye basana kadar oynatma = !azaltılmış hareket.
  const reducedMotion = useSyncExternalStore(subscribeReducedMotion, () => window.matchMedia(REDUCED_MOTION).matches, () => true);
  const hydrated = useSyncExternalStore(noopSubscribe, () => true, () => false);
  const [userPlaying, setUserPlaying] = useState<boolean | null>(null);
  const playing = userPlaying ?? !reducedMotion;
  const [hovered, setHovered] = useState(false);
  const [focusPaused, setFocusPaused] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  // Yüklenecek slaytlar: ilk başta yalnızca ilki; sonra etkin slaytın komşuları
  const rootRef = useRef<HTMLDivElement>(null);
  const remaining = useRef(INTERVAL_MS);
  const swipe = useRef<{ x: number; y: number } | null>(null);

  const running = playing && !hovered && !focusPaused && onScreen && pageVisible && total > 1;

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    const onVisibility = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Yüklenen slaytlar: sunucu çiziminde yalnızca ilki (LCP); hidrasyondan sonra etkin slayt ve iki komşusu
  // (geçişte boş kare görünmez). Bir kez yüklenen fotoğraf tarayıcı önbelleğinden gelir.
  const shouldLoad = (i: number) => seen.has(i) || (hydrated && (i === (index + 1) % total || i === (index - 1 + total) % total));

  // Yeni slaytta süre baştan; duraklatılıp sürdürülünce kalan süreden devam (ilerleme çubuğuyla aynı)
  useEffect(() => {
    remaining.current = INTERVAL_MS;
  }, [index]);

  useEffect(() => {
    if (!running) return;
    const start = performance.now();
    const timer = window.setTimeout(() => setIndex((index + 1) % total), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (performance.now() - start));
    };
  }, [running, index, total, setIndex]);

  /** Kullanıcı gezinmesi: otomatik geçiş durur (WCAG 2.2.2; APG) */
  const goTo = useCallback(
    (i: number) => {
      setUserPlaying(false);
      setIndex(((i % total) + total) % total);
    },
    [total, setIndex],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys: Record<string, number> = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: total - 1 };
    if (!(e.key in keys) || e.altKey || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    goTo(keys[e.key]);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse") return;
    swipe.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(e.clientY - s.y)) goTo(dx < 0 ? index + 1 : index - 1);
  };

  const togglePlay = () => {
    // Odak düğmedeyken "Başlat" denirse geçiş hemen başlasın (odak duraklatması yalnızca odak yeniden girince)
    setFocusPaused(false);
    setUserPlaying(!playing);
  };

  const label = (n: number) => fmt(labels.slide, { n, total });
  const goToLabel = (n: number) => fmt(labels.goTo, { n, total });
  const roundButton =
    "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface text-ink shadow-card ring-1 ring-line transition-colors hover:bg-primary-soft";

  return (
    <div
      ref={rootRef}
      role="region"
      aria-roledescription={labels.roleCarousel}
      aria-label={labels.label}
      className={className}
      onKeyDown={onKeyDown}
      onPointerEnter={(e) => e.pointerType === "mouse" && setHovered(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setHovered(false)}
      onFocus={(e) => {
        // Yalnızca klavye odağı (fareyle düğmeye tıklamak geçişi durdurmaz; tıklama zaten düğmenin işini yapar)
        if (e.target.matches(":focus-visible")) setFocusPaused(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusPaused(false);
      }}
    >
      <div className="relative">
        <div
          aria-live={running ? "off" : "polite"}
          className="relative aspect-[16/10] touch-pan-y overflow-hidden rounded-xl bg-surface-alt shadow-raised ring-1 ring-line select-none"
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={() => (swipe.current = null)}
        >
          <div
            className="flex h-full transition-transform duration-700 ease-[cubic-bezier(0.22,0.61,0.36,1)] motion-reduce:transition-none"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {images.map((img, i) => (
              <div
                key={img.src}
                role="group"
                aria-roledescription={labels.roleSlide}
                aria-label={label(i + 1)}
                aria-hidden={i !== index}
                inert={i !== index}
                className="relative h-full w-full shrink-0"
              >
                {shouldLoad(i) && (
                  <Image
                    src={img.src}
                    alt={img.alt}
                    fill
                    sizes={sizes}
                    draggable={false}
                    loading="eager"
                    fetchPriority={i === 0 ? "high" : undefined}
                    className="object-cover"
                  />
                )}
              </div>
            ))}
          </div>
          {/* İlerleme çubuğu: dekoratif; azaltılmış harekette ve dururken yok */}
          {playing && !reducedMotion && (
            <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-surface/45">
              <span
                key={index}
                className="block h-full origin-left animate-gallery-progress bg-wave"
                style={{ animationPlayState: running ? "running" : "paused" }}
              />
            </span>
          )}
        </div>
        <button type="button" onClick={() => goTo(index - 1)} aria-label={labels.prev} className={`${roundButton} absolute top-1/2 left-3 -translate-y-1/2`}>
          <IconChevronLeft size={22} />
        </button>
        <button type="button" onClick={() => goTo(index + 1)} aria-label={labels.next} className={`${roundButton} absolute top-1/2 right-3 -translate-y-1/2`}>
          <IconChevronRight size={22} />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <button type="button" onClick={togglePlay} aria-label={playing ? labels.pause : labels.play} className={roundButton}>
          {playing ? <IconPause /> : <IconPlay />}
        </button>
        {/* Mobil ve tablet: noktalar (dokunma alanı 44 px yüksek); masaüstü: küçük resimler */}
        <ul aria-label={labels.picker} className="flex min-w-0 flex-1 flex-wrap items-center justify-center lg:hidden">
          {images.map((img, i) => (
            <li key={img.src}>
              <button
                type="button"
                onClick={() => goTo(i)}
                aria-label={goToLabel(i + 1)}
                aria-current={i === index ? "true" : undefined}
                className="group/dot inline-flex h-11 w-7 items-center justify-center rounded-full"
              >
                {/* Geçiş animasyonu yok: width ve background-color animasyonları birleştirilemez (her otomatik geçişte ana
                    iş parçacığında yerleşim/boyama; Lighthouse "Birleştirilmemiş animasyonlardan kaçının") */}
                <span
                  aria-hidden="true"
                  className={`block h-2 rounded-full ${i === index ? "w-5 bg-ink" : "w-2 bg-line-strong group-hover/dot:bg-ink-muted"}`}
                />
              </button>
            </li>
          ))}
        </ul>
        <ul aria-label={labels.picker} className="hidden min-w-0 flex-1 gap-2 p-1 lg:flex">
          {images.map((img, i) => (
            // Küçük resimler satıra sığacak kadar daralır (10 fotoğrafta da taşma yok). Seçili halka animasyonsuz değişir
            // (box-shadow geçişi birleştirilemez; otomatik geçişte her 6 sn'de bir)
            <li key={img.src} className="max-w-[4.75rem] min-w-0 flex-1">
              <button
                type="button"
                onClick={() => goTo(i)}
                aria-label={goToLabel(i + 1)}
                aria-current={i === index ? "true" : undefined}
                className={`relative block aspect-[16/10] w-full overflow-hidden rounded-md ring-offset-2 ring-offset-surface ${i === index ? "ring-2 ring-ink" : "ring-1 ring-line hover:ring-line-strong"}`}
              >
                <Image src={img.src} alt="" fill sizes="72px" quality={50} loading="lazy" className="object-cover" />
              </button>
            </li>
          ))}
        </ul>
        <p aria-hidden="true" className="tabular shrink-0 text-sm font-semibold text-ink-muted">
          {index + 1} / {total}
        </p>
      </div>
    </div>
  );
}

function IconPause() {
  return (
    <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

function IconPlay() {
  return (
    <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.2-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" />
    </svg>
  );
}
